import {
  getAuroraJurisdiction,
  getAuroraJurisdictionBySite,
  getSiteValue,
} from '../../../../data/contactCenter/aurora/dropDownOptions';

/** Contact + formatting + status helpers consolidated
 *  Combined: previous utils.js, contactFormatHelpers.js, statusHelpers.js
 */

export function getRetailerRepsFromRepInfoResponse(repInfo) {
  if (!repInfo) {
    return [];
  }

  const representatives = repInfo.modifiableRepresentatives ?? [];
  const retailerReps = [];

  for (let i = 0; i < representatives.length; i++) {
    const rep = representatives[i].representative;
    if (rep) {
      retailerReps.push({
        ...rep,
        representativeType: representatives[i].representativeType,
      });
    }
  }

  if (retailerReps.length) {
    return retailerReps;
  }

  // Texas v2 returns reps keyed by type ("4" = OSR, "8" = LSR).
  if (!Array.isArray(repInfo) && typeof repInfo === 'object') {
    return Object.entries(repInfo).map(([typeKey, rep]) => ({
      ...rep,
      representativeType: rep?.representativeType ?? Number(typeKey),
    }));
  }

  return retailerReps;
}

function contactHasDisplayInfo(contact) {
  return (
    contact.firstName ||
    contact.lastName ||
    contact.workEmail ||
    contact.workPhone
  );
}

function toPascalCaseName(name) {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const trimmed = name.trim();
  if (!trimmed) {
    return '';
  }

  const words = trimmed.split(/\s+/);
  const formattedWords = [];

  for (let i = 0; i < words.length; i++) {
    const word = words[i].toLowerCase();
    formattedWords.push(word.charAt(0).toUpperCase() + word.slice(1));
  }

  return formattedWords.join(' ');
}

function buildRepContact(rep) {
  let firstName = rep.firstName || '';
  let lastName = rep.lastName || '';
  let workEmail = rep.workEmail || '';
  let workPhone = rep.workPhone || '';
  
  const repTypeLabels = { 4: 'OSR', 8: 'LSR' };
  const repType =
    repTypeLabels[rep.representativeType] || 'No Representative Type';

  let representativeOid = rep.representativeOid
    ? rep.representativeOid
    : rep.userOid
      ? rep.userOid
      : rep.esteId;

  return {
    representativeOid,
    firstName: toPascalCaseName(firstName),
    lastName: toPascalCaseName(lastName),
    repType,
    workEmail,
    workPhone: isValidContactPhone(workPhone) ? String(workPhone).trim() : '',
  };
}

export function removeDuplicateContacts(contacts) {
  const uniqueContacts = [];
  const seenContactIds = [];

  for (let i = 0; i < contacts.length; i++) {
    const contact = contacts[i];
    const contactId = contact.representativeOid || contact.workEmail;

    if (seenContactIds.indexOf(contactId) === -1) {
      seenContactIds.push(contactId);
      uniqueContacts.push(contact);
    }
  }

  return uniqueContacts;
}

export function repListToContacts(repList) {
  const contacts = [];

  for (let i = 0; i < repList.length; i++) {
    const contact = buildRepContact(repList[i]);
    if (contact && contactHasDisplayInfo(contact)) {
      contacts.push(contact);
    }
  }

  return contacts;
}

export function formatContactDisplayName(contact) {
  const lastName = contact.lastName || '';
  const firstName = contact.firstName || '';

  if (lastName && firstName) {
    return `${lastName}, ${firstName}`;
  }

  if (lastName) {
    return lastName;
  }

  return firstName;
}

export function sortContactsByLastName(contacts) {
  const sortedContacts = [...contacts];

  sortedContacts.sort((contactA, contactB) => {
    const lastNameA = (contactA.lastName || '').toLowerCase();
    const lastNameB = (contactB.lastName || '').toLowerCase();

    if (!lastNameA && !lastNameB) {
      return 0;
    }
    if (!lastNameA) {
      return 1;
    }
    if (!lastNameB) {
      return -1;
    }

    return lastNameA.localeCompare(lastNameB);
  });

  return sortedContacts;
}

/* --------------------- Terminal helpers --------------------- */

function trimEsConnectTerminalId(esConnectTerminalId) {
  return String(esConnectTerminalId || '').trim();
}

export function getRetailerOidFromInfo(retailerInfo) {
  if (!retailerInfo) {
    return '';
  }

  if (retailerInfo.retailerOid != null && retailerInfo.retailerOid !== '') {
    return String(retailerInfo.retailerOid).trim();
  }

  return '';
}

export function getTerminalOid(terminal) {
  if (!terminal) {
    return '';
  }

  if (terminal.terminalOid != null && terminal.terminalOid !== '') {
    return String(terminal.terminalOid).trim();
  }

  return '';
}

export function getEsConnectTerminalId(terminal) {
  return trimEsConnectTerminalId(terminal?.esConnectTerminalId);
}

export function getTerminalDisplayId(terminal) {
  if (!terminal) {
    return '—';
  }

  if (terminal.terminalId != null && terminal.terminalId !== '') {
    return String(terminal.terminalId);
  }

  const oid = getTerminalOid(terminal);
  return oid || '—';
}

export function findTerminalInList(list, terminal) {
  if (!list?.length || !terminal) {
    return null;
  }

  const oid = getTerminalOid(terminal);
  if (oid) {
    const byOid = list.find(item => getTerminalOid(item) === oid);
    if (byOid) {
      return byOid;
    }
  }

  if (terminal.terminalId != null && terminal.terminalId !== '') {
    return (
      list.find(
        item => String(item.terminalId) === String(terminal.terminalId),
      ) ?? null
    );
  }

  return null;
}

export function validateTerminalAction(terminal, site, actionDef) {
  if (!site) {
    return 'Jurisdiction/site is missing. Please select a jurisdiction and search again.';
  }

  if (actionDef.requiresTerminalOid && !getTerminalOid(terminal)) {
    return 'Terminal OID is missing. Please refresh terminal info and try again.';
  }

  if (actionDef.requiresEsConnect && !getEsConnectTerminalId(terminal)) {
    return 'esConnect Terminal ID is missing. This action cannot be run for this terminal.';
  }

  return null;
}

export function getTerminalTypeLabel(terminalType, descriptionLookup) {
  if (terminalType == null || terminalType === '') {
    return '';
  }

  const description = descriptionLookup?.[String(terminalType)];
  if (description) {
    return description;
  }

  return `Type ${terminalType}`;
}

export function formatSignOnStatusLabel(signonStatus) {
  if (signonStatus == null || signonStatus === '') {
    return '—';
  }

  return String(signonStatus)
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

export function formatSignOnTimestamp(timestamp) {
  if (timestamp == null || timestamp === '') {
    return '—';
  }

  const trimmed = String(timestamp).trim();
  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/i,
  );

  if (!match) {
    return trimmed;
  }

  const [, year, month, day, hour, minute] = match;
  const monthDate = new Date(Number(year), Number(month) - 1, 1);
  const monthLabel = monthDate.toLocaleString('en-US', { month: 'short' });
  const hourNum = Number(hour);
  const hour12 = hourNum % 12 || 12;
  const ampm = hourNum >= 12 ? 'PM' : 'AM';

  return `${monthLabel} ${Number(day)}, ${year}, ${hour12}:${minute} ${ampm}`;
}

const FORM_NAME_DISPLAY_OVERRIDES = {
  its_orders: 'ITS Orders',
};

const RECENT_CONSUMABLE_FORM_NAMES = new Set([
  'consumable_orders',
  'its_orders',
]);

export function formatFormName(formName) {
  if (formName == null || formName === '') {
    return '—';
  }

  const normalized = String(formName).trim().toLowerCase();
  if (FORM_NAME_DISPLAY_OVERRIDES[normalized]) {
    return FORM_NAME_DISPLAY_OVERRIDES[normalized];
  }

  // Turn API keys like "consumable_orders" into spaced words: "consumable orders".
  return String(formName)
    .replace(/_/g, ' ')
    // Title-case the first letter of each word: "consumable orders" -> "Consumable Orders".
    .replace(/\b\w/g, character => character.toUpperCase());
}

export function mapRecentFormsFromApi(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter(
      row =>
        !RECENT_CONSUMABLE_FORM_NAMES.has(
          String(row.formName ?? '')
            .trim()
            .toLowerCase(),
        ),
    )
    .map((row, index) => ({
      id: `${row.formName}-${row.date}-${index}`,
      submissionTime: formatSignOnTimestamp(row.date),
      formName: formatFormName(row.formName),
      submitter: row.displayName ?? '—',
    }));
}

function getFormOrigin(userId) {
  return userId == null || userId === '' ? 'Self-Service' : 'Agent Assisted';
}

export function mapRecentConsumablesFromApi(rows) {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const filtered = sourceRows.filter(row =>
    RECENT_CONSUMABLE_FORM_NAMES.has(
      String(row.formName ?? '')
        .trim()
        .toLowerCase(),
    ),
  );
  const mapped = filtered.slice(0, 5).map((row, index) => ({
    id: `${row.formName}-${row.date}-${index}`,
    submissionTime: formatSignOnTimestamp(row.date),
    formName: formatFormName(row.formName),
    origin: getFormOrigin(row.userId),
    submitter:
      row.userId == null || row.userId === ''
        ? '—'
        : (row.displayName ?? '—'),
  }));

  return mapped;
}

/* --------------------- Contact formatting helpers --------------------- */

/** Country strings treated as United States for phone country-code handling. */
const US_COUNTRY_VALUES = new Set([
  'us',
  'usa',
  'u.s.',
  'u.s.a.',
  'united states',
  'united states of america',
]);

function isUSCountryValue(value) {
  if (value == null || value === '') {
    return false;
  }

  return US_COUNTRY_VALUES.has(String(value).trim().toLowerCase());
}

function normalizePhoneDigits(phone) {
  return String(phone ?? '').replace(/\D/g, '');
}

export function isValidContactPhone(phone) {
  const trimmedPhone = String(phone ?? '').trim();
  if (!trimmedPhone) {
    return false;
  }

  const digitsOnly = normalizePhoneDigits(trimmedPhone);
  if (!digitsOnly) {
    return false;
  }

  if (/^0+$/.test(digitsOnly)) {
    return false;
  }

  if (/^(\d)\1+$/.test(digitsOnly)) {
    return false;
  }

  return digitsOnly.length >= 10;
}

export function isUSRetailer(retailerLocationOrSite) {
  if (retailerLocationOrSite == null || retailerLocationOrSite === '') {
    return false;
  }

  if (typeof retailerLocationOrSite === 'string') {
    const value = retailerLocationOrSite.trim();
    if (!value) {
      return false;
    }

    if (getAuroraJurisdiction(value) || getAuroraJurisdictionBySite(value)) {
      return true;
    }

    return isUSCountryValue(value);
  }

  if (typeof retailerLocationOrSite === 'object') {
    const site =
      retailerLocationOrSite.site ??
      getSiteValue(
        retailerLocationOrSite.jurisdiction ?? retailerLocationOrSite.label,
      );

    if (site && getAuroraJurisdictionBySite(site)) {
      return true;
    }

    const jurisdictionLabel =
      retailerLocationOrSite.jurisdiction ?? retailerLocationOrSite.label;

    if (jurisdictionLabel && getAuroraJurisdiction(jurisdictionLabel)) {
      return true;
    }

    if (
      isUSCountryValue(
        retailerLocationOrSite.country ?? retailerLocationOrSite.countryCode,
      )
    ) {
      return true;
    }
  }

  return false;
}

export function formatEmail(email) {
  if (email == null || email === undefined) {
    return '';
  }

  const trimmedEmail = String(email).trim();
  if (!trimmedEmail) {
    return '';
  }

  return trimmedEmail.toLowerCase();
}

export function formatPhoneForDisplay(phone, retailerLocationOrSite) {
  if (!isValidContactPhone(phone)) {
    return '';
  }

  const trimmedPhone = String(phone).trim();
  const hasReadableFormatting = /[^\d+\s]/.test(trimmedPhone);

  if (hasReadableFormatting) {
    return trimmedPhone;
  }

  const digitsOnly = normalizePhoneDigits(trimmedPhone);

  if (isUSRetailer(retailerLocationOrSite) && digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  }

  return trimmedPhone;
}

export function formatPhoneHref(phone, retailerLocationOrSite) {
  if (!isValidContactPhone(phone)) {
    return '';
  }

  const digitsOnly = normalizePhoneDigits(phone);
  const trimmedPhone = String(phone).trim();

  if (trimmedPhone.startsWith('+')) {
    return `tel:${trimmedPhone.replace(/[^\d+]/g, '')}`;
  }

  if (isUSRetailer(retailerLocationOrSite)) {
    if (digitsOnly.length === 10) {
      return `tel:+1${digitsOnly}`;
    }

    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `tel:+${digitsOnly}`;
    }
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `tel:+${digitsOnly}`;
  }

  return `tel:+${digitsOnly}`;
}

// inventory review helpers

export const INVENTORY_REVIEW_STATUS_LABELS = {
  1: 'Available',
  2: 'Issued',
  3: 'Confirmed',
  4: 'Activated',
  5: 'Settled',
  10: 'Stolen Retailer Non-Chargeable',
  41: 'In Lane Initiated'
};

export function formatInventoryReviewStatus(statusId) {
  if (statusId == null || statusId === '') {
    return '—';
  }
  return INVENTORY_REVIEW_STATUS_LABELS[statusId] || statusId;
}

export function getHostEntityStatusCode(entity) {
  if (!entity) {
    return null;
  }

  if (entity.statusCode != null && entity.statusCode !== '') {
    return entity.statusCode;
  }

  if (entity.status != null && entity.status !== '') {
    return entity.status;
  }

  return null;
}

export function getStatusChipMeta(statusCode, translations = {}) {
  if (statusCode == null || statusCode === '') {
    return {
      label: translations.statusUnavailable || 'Unknown',
      chipVariant: 'outlined',
      chipSx: {
        fontWeight: 600,
        borderColor: 'var(--reach-blue)',
        color: 'var(--reach-blue)',
      },
      isActive: false,
    };
  }

  const code = Number(statusCode);
  if (code === 2) {
    return {
      label: translations.active || 'Active',
      chipVariant: 'filled',
      chipSx: {
        backgroundColor: 'var(--procyon-green)',
        color: 'var(--reach-blue)',
        fontWeight: 600,
      },
      isActive: true,
    };
  }

  if (code === 1 || code === 4) {
    return {
      label: translations.inactive || 'Inactive',
      chipVariant: 'filled',
      chipSx: {
        backgroundColor: 'var(--arcturus-red)',
        color: 'var(--reach-blue)',
        fontWeight: 600,
      },
      isActive: false,
    };
  }

  if (code === 3) {
    return {
      label: translations.cancelled || 'Cancelled',
      chipVariant: 'outlined',
      chipSx: {
        backgroundColor: 'white',
        color: 'var(--arcturus-red)',
        fontWeight: 600,
      },
      isActive: false,
    };
  }

  return {
    label: String(statusCode),
    chipVariant: 'outlined',
    chipSx: {
      fontWeight: 600,
      borderColor: 'var(--reach-blue)',
      color: 'var(--reach-blue)',
    },
    isActive: false,
  };
}
